import { Component, inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import memo from 'memo-decorator';
import { BehaviorSubject, combineLatest, zip } from 'rxjs';
import { combineLatestWith, finalize, map, take } from 'rxjs/operators';

import { TransactionLpResponse } from 'src/app/lp/models/transactionLpResponse.model';
import { TransactionStatus } from 'src/app/lp/models/transactionStatus.enum';
import { LpInvestingEntityDataService } from 'src/app/services/lp/lp-investing-entity-data.service';
import { UtilsService } from 'src/app/services/utils.service';
import { DistributionTransactionPurpose } from 'src/app/shared/enums/DistributionTransactionPurpose.enum';
import { TerraUtils } from 'src/app/shared/TerraUtils';
import { AnalyticsServiceNameModel, TelemetryService } from 'telemetry-library';
import { LpTransactionDetailsDialogComponent } from '../../../shared/lp-transaction-details-dialog/lp-transaction-details-dialog.component';
import { LpFundService } from '../lp-fund.service';

@Component({
  selector: 'terra-lp-fund-transactions-tab',
  templateUrl: './lp-fund-transactions-tab.component.html',
  styleUrls: ['../../../styles/holding/holding-page-shared.scss', './lp-fund-transactions-tab.component.scss']
})
export class LpFundTransactionsTabComponent implements OnInit {

  // Enums
  readonly TransactionStatus = TransactionStatus;
  readonly DistributionType = DistributionTransactionPurpose;

  lpInvestingEntityDataService = inject(LpInvestingEntityDataService)
  filter$: BehaviorSubject<Number[] | null> = new BehaviorSubject(null);
  transactions$ = combineLatest([this.lpFundService.transactions$, this.filter$])
  .pipe(
    map(([transactions, filter]) => 
      filter ? 
        transactions.filter(transaction => filter.includes(transaction.investingEntityId) && this.isTransactionDisplayable(transaction))
      : transactions.filter(this.isTransactionDisplayable))
    );
    
  isGeneratingExportFile$ = new BehaviorSubject(false);
  showGpPromote$ = this.lpFundService.holdingDetails$.pipe(map(h => h.showGpPromoteToLPs));

investingEntities$ = zip(
    this.lpInvestingEntityDataService.getInvestingEntitiesByCurrentClient(),
    this.lpInvestingEntityDataService.getSharedInvestingEntitiesForCurrentLp()
  ).pipe(
    combineLatestWith(this.lpFundService.fund$),
    map(([[own = [], shared = []], holding]) => {
      const allEntities = [...own];
      let filterEntities = [];

      shared?.forEach((entity) => allEntities.push(entity));
      allEntities.forEach((entity) => {
        if (!filterEntities.some((f) => f.id === entity.id)) {
          filterEntities.push(entity);
        }
      });

      const availableEntities = holding.investingEntitiesIds || [];

      filterEntities = filterEntities.filter((item) =>
        availableEntities.includes(item.id)
      );

      this.filter([], filterEntities);
      return filterEntities;
    })
  );

  constructor(
    private lpFundService: LpFundService,
    private matDialog: MatDialog,
    private utilsService: UtilsService,
    private telemetryService: TelemetryService
  ) {
  }

  ngOnInit() {
    this.lpFundService.holdingId$.pipe(take(1)).subscribe(fundId => {
      this.telemetryService.create({
        eventID: '204',
        eventTitle: 'LP VIEWED TRANSACTIONS TAB',
        holdingID: fundId,
        additional: {
          isMobileDevice: TerraUtils.isMobileDevice()
        }
      }, AnalyticsServiceNameModel.Insights);
    });
  }

  showBreakdownDialog(transaction: TransactionLpResponse) {
    const dialogConfig = new MatDialogConfig<TransactionLpResponse>();
    this.lpFundService.setTransactionDialogConfig(dialogConfig);
    this.showGpPromote$.pipe(
      map(sgp => {
        transaction.showGpPromote = sgp;
        dialogConfig.data = transaction;
        this.matDialog.open(LpTransactionDetailsDialogComponent, dialogConfig);
      })
    ).subscribe();
  }

  getAmountAsync(transaction: TransactionLpResponse) {
    return this.showGpPromote$.pipe(
      map(sgp => transaction.amountGross - (sgp ? 0 : (transaction.gpPromote || 0)))
    );
  }

  @memo()
  getCurrency(transaction: TransactionLpResponse) {
    return transaction.currency;
  }

  export() {
    this.telemetryService.create({
      eventID: '201',
      eventTitle: 'LP FUND - EXPORT TRANSACTIONS',
      additional: {
        isMobileDevice: TerraUtils.isMobileDevice()
      }
    }, AnalyticsServiceNameModel.Insights);
    this.isGeneratingExportFile$.next(true);
    this.lpFundService.transactionsExcelUrl$
      .pipe(finalize(() => this.isGeneratingExportFile$.next(false)))
      .subscribe(
        fileUrl => {
          if (fileUrl) {
            this.utilsService.downloadFileFromUrl(fileUrl);
          }
        },
        error => {

        });
  }

  isClickableTransaction(transaction: TransactionLpResponse) {
    return !transaction.isOutgoing || transaction.remarks?.length > 0;
  }

  filter(items, allItems = []) {
    const ids = (items.length ? items : allItems)?.map((item) => item.id);
    this.filter$.next(ids);
  }

  isTransactionDisplayable(transaction: TransactionLpResponse) {
    return (transaction.amountGross || 0) - (transaction.gpPromote || 0) > 0;
  }
}
